import {
  AppCard,
  Icon,
  PageContainer,
  RouteButtonLink,
  RouteIconButtonLink,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiClose, mdiEye, mdiHelpCircleOutline, mdiPlus } from "@mdi/js";
import { TabContext } from "@mui/lab";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { DeliveryDateChangeRequestIndex } from "../../../@types/services/DeliveryDateChangeRequest";
import { VendorBadge } from "../../../components/atoms/VendorBadge";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { useAuth } from "../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";
import { ApproveButtons } from "./-internal_components/ApproveButtons";
import { ArchiveButton } from "./-internal_components/ArchiveButton";
import { DeliveryDateChangeRequestFilters } from "./-internal_components/DeliveryDateChangeRequestFilters";
import { RetryButton } from "./-internal_components/RetryButton";
import { deliveryDateChangeRequestsListQueryOptions } from "./-queries";

const searchSchema = z.object({
  brazilianVendor: z.optional(z.boolean()).catch(undefined),
  createdById: z.optional(z.array(z.coerce.number())).catch(undefined),
  page: z.optional(z.coerce.number()).catch(undefined),
  plantId: z.optional(z.array(z.coerce.string())).catch(undefined),
  procurementResponsibleId: z
    .optional(z.array(z.coerce.number()))
    .catch(undefined),
  purchaseGroupId: z.optional(z.array(z.coerce.string())).catch(undefined),
  purchaseOrderItemNumber: z.optional(z.coerce.number()).catch(undefined),
  purchaseOrderNumber: z.optional(z.coerce.number()).catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  tab: z
    .enum(["pending_approval", "rejected", "processing", "completed", "failed"])
    .default("pending_approval")
    .catch("pending_approval"),
  vendorId: z.optional(z.array(z.coerce.number())).catch(undefined),
});

type TabType = z.infer<typeof searchSchema.shape.tab>;

const getStatusFilter = (value: TabType): string[] | string => {
  if (value === "processing") {
    return ["processing", "approved"];
  }
  if (value === "completed") {
    return ["completed", "archived"];
  }
  return value;
};

const DeliveryDateChangeRequestTableRow: React.FC<{
  deliveryDateChangeRequest: DeliveryDateChangeRequestIndex;
  isSelected: boolean;
  onSelect: (id: string) => void;
  showCheckbox: boolean;
  tabValue: string;
}> = ({
  deliveryDateChangeRequest,
  isSelected,
  onSelect,
  showCheckbox,
  tabValue,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "completed":
        return t("deliveryDateChangeRequests.labels.updatedOnSap");
      case "archived":
        return t("deliveryDateChangeRequests.labels.archived");
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): "success" | "default" => {
    switch (status) {
      case "completed":
        return "success";
      case "archived":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <TableRow key={deliveryDateChangeRequest.id}>
      {!!showCheckbox && (
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect(deliveryDateChangeRequest.id)}
          />
        </TableCell>
      )}
      <TableCell>
        {deliveryDateChangeRequest.purchase_order_item.purchase_order.po_number}
      </TableCell>
      <TableCell>
        {deliveryDateChangeRequest.purchase_order_item.po_item_number}
      </TableCell>
      <TableCell>
        {t(
          "intlDateTime",
          i18nextParseToDate(deliveryDateChangeRequest.created_at),
        )}
      </TableCell>
      <TableCell>
        <VendorBadge
          avatarAlignment="center"
          showSubInfo={false}
          vendorId={
            deliveryDateChangeRequest.purchase_order_item.purchase_order.vendor
              .id
          }
        />
      </TableCell>
      <TableCell>
        <Tooltip
          title={deliveryDateChangeRequest.purchase_order_item.plant.name}
        >
          <Typography variant="body2">
            {deliveryDateChangeRequest.purchase_order_item.plant.plant_number}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip
          title={
            deliveryDateChangeRequest.purchase_order_item.purchase_order
              .purchase_group.description
          }
        >
          <Typography variant="body2">
            {
              deliveryDateChangeRequest.purchase_order_item.purchase_order
                .purchase_group.name
            }
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {t(
          "intlDate",
          i18nextParseToDate(
            deliveryDateChangeRequest.purchase_order_item.purchase_order.vendor
              .brazilian
              ? deliveryDateChangeRequest.delivery_date_at_request
              : deliveryDateChangeRequest.statistical_delivery_date_at_request,
          ),
        )}
      </TableCell>
      <TableCell>
        {deliveryDateChangeRequest.purchase_order_item.purchase_order.vendor
          .brazilian
          ? t(
              "intlDate",
              i18nextParseToDate(deliveryDateChangeRequest.delivery_date),
            )
          : deliveryDateChangeRequest.statistical_delivery_date &&
            t(
              "intlDate",
              i18nextParseToDate(
                deliveryDateChangeRequest.statistical_delivery_date,
              ),
            )}
      </TableCell>
      <TableCell>
        {tabValue === "completed" ? (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
            }}
          >
            <Chip
              color={getStatusColor(deliveryDateChangeRequest.status)}
              label={getStatusLabel(deliveryDateChangeRequest.status)}
              size="small"
              variant="filled"
            />
            {user?.roles_names.includes(
              "Delivery Date Change Request - View",
            ) && (
              <RouteIconButtonLink
                params={{
                  deliveryDateChangeRequestId: deliveryDateChangeRequest.id,
                }}
                to="/delivery_date_change_requests/$deliveryDateChangeRequestId"
              >
                <Icon icon={mdiEye} />
              </RouteIconButtonLink>
            )}
          </Box>
        ) : (
          user?.roles_names.includes("Delivery Date Change Request - View") && (
            <RouteIconButtonLink
              params={{
                deliveryDateChangeRequestId: deliveryDateChangeRequest.id,
              }}
              to="/delivery_date_change_requests/$deliveryDateChangeRequestId"
            >
              <Icon icon={mdiEye} />
            </RouteIconButtonLink>
          )
        )}
      </TableCell>
    </TableRow>
  );
};

const DeliveryDateChangeRequestTable: React.FC = () => {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const { page = 1, rowsPerPage = 25, tab } = Route.useSearch();

  const loaderDeps = Route.useLoaderDeps();
  const { data: deliveryDateChangeRequestsResponse, isLoading } =
    useSuspenseQuery(deliveryDateChangeRequestsListQueryOptions(loaderDeps));

  const { user } = useAuth();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const showCheckboxes = useMemo(
    () => tab === "pending_approval" || tab === "failed",
    [tab],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need reset the selected items when tab is changed
  useEffect(() => {
    setSelectedItems([]);
  }, [tab]);

  useEffect(() => {
    if (!deliveryDateChangeRequestsResponse) return;

    const { current_page: currentPage, items_per_page: currentItemsPerPage } =
      deliveryDateChangeRequestsResponse;

    if (currentPage !== page || currentItemsPerPage !== rowsPerPage) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          page: currentPage,
          rowsPerPage: currentItemsPerPage,
        }),
      });
    }
  }, [deliveryDateChangeRequestsResponse, page, rowsPerPage, navigate]);

  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: newPage + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  const deliveryDateChangeRequests = useMemo(
    () => deliveryDateChangeRequestsResponse?.results || [],
    [deliveryDateChangeRequestsResponse],
  );

  const isSelected = useCallback(
    (id: string) => selectedItems.includes(id),
    [selectedItems],
  );

  const isAllSelected = useMemo(
    () =>
      deliveryDateChangeRequests.length > 0 &&
      selectedItems.length === deliveryDateChangeRequests.length,
    [deliveryDateChangeRequests.length, selectedItems.length],
  );

  const handleSelectItem = useCallback(
    (id: string) => {
      if (isSelected(id)) {
        setSelectedItems(selectedItems.filter((itemId) => itemId !== id));
      } else {
        setSelectedItems([...selectedItems, id]);
      }
    },
    [isSelected, selectedItems],
  );

  const handleSelectAllClick = useCallback(() => {
    if (isAllSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(deliveryDateChangeRequests.map((item) => item.id));
    }
  }, [deliveryDateChangeRequests, isAllSelected]);

  const handleDecisionComplete = useCallback(() => {
    setSelectedItems([]);
  }, []);

  return (
    <Box>
      {selectedItems.length > 0 && showCheckboxes && (
        <Box
          sx={{
            bgcolor: "tokens.mutedSurface",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginY: 0,
            p: 1,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <IconButton onClick={() => setSelectedItems([])}>
              <Icon icon={mdiClose} />
            </IconButton>
            <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
              {t("common.selectedItems", {
                count: selectedItems.length,
              })}
            </Typography>
            {tab === "pending_approval" &&
              user?.roles_names.includes(
                "Delivery Date Change Request - Approve",
              ) && (
                <ApproveButtons
                  deliveryDateChangeRequestIds={selectedItems}
                  onDecisionComplete={handleDecisionComplete}
                />
              )}
            {tab === "pending_approval" && (
              <ArchiveButton
                deliveryDateChangeRequestIds={selectedItems}
                onArchiveComplete={handleDecisionComplete}
              />
            )}
            {tab === "failed" &&
              user?.roles_names.includes(
                "Delivery Date Change Request - Approve",
              ) && (
                <>
                  <RetryButton
                    deliveryDateChangeRequestIds={selectedItems}
                    onRetryComplete={handleDecisionComplete}
                  />
                  <ArchiveButton
                    deliveryDateChangeRequestIds={selectedItems}
                    onArchiveComplete={handleDecisionComplete}
                  />
                </>
              )}
          </Box>
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {showCheckboxes && (
                <TableCell padding="checkbox">
                  <Tooltip
                    title={
                      isAllSelected
                        ? t("common.uncheckAll")
                        : t("common.checkAll")
                    }
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedItems.length > 0 &&
                        selectedItems.length < deliveryDateChangeRequests.length
                      }
                      onChange={handleSelectAllClick}
                    />
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>
                {t("deliveryDateChangeRequests.labels.poNumber")}
              </TableCell>
              <TableCell>
                {t("deliveryDateChangeRequests.labels.poItemNumber")}
              </TableCell>
              <TableCell>{t("common.createdAt")}</TableCell>
              <TableCell>{t("common.vendor")}</TableCell>
              <TableCell>{t("common.plant")}</TableCell>
              <TableCell>{t("common.purchaseGroup")}</TableCell>
              <TableCell sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                {t("deliveryDateChangeRequests.labels.deliveryDateAtRequest")}
                <Tooltip
                  title={t(
                    "deliveryDateChangeRequests.messages.deliveryDateAtRequestHelpText",
                  )}
                >
                  <Icon
                    fontSize="small"
                    icon={mdiHelpCircleOutline}
                    sx={{
                      marginTop: "-4px", // To align icon with text
                      opacity: 0.5,
                    }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>
                {t("deliveryDateChangeRequests.labels.requestedDate")}
              </TableCell>
              <TableCell>
                {tab === "completed" ? t("common.status") : ""}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={showCheckboxes ? 8 : 7}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            ) : (
              deliveryDateChangeRequests.map((deliveryDateChangeRequest) => (
                <DeliveryDateChangeRequestTableRow
                  deliveryDateChangeRequest={deliveryDateChangeRequest}
                  isSelected={isSelected(deliveryDateChangeRequest.id)}
                  key={deliveryDateChangeRequest.id}
                  onSelect={handleSelectItem}
                  showCheckbox={showCheckboxes}
                  tabValue={tab}
                />
              ))
            )}
          </TableBody>
          {!!deliveryDateChangeRequestsResponse && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={deliveryDateChangeRequestsResponse.total_items}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  page={page - 1}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  showFirstButton
                  showLastButton
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

const DeliveryDateChangeRequest: React.FC = () => {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const { tab } = Route.useSearch();

  const { user } = useAuth();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton={false}
      title={t("deliveryDateChangeRequests.titles.index")}
    >
      <AppCard>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} sx={{ margin: 2 }} />
              </Box>
            }
          >
            <DeliveryDateChangeRequestFilters />
          </Suspense>
          {user?.roles_names.includes(
            "Delivery Date Change Request - Create",
          ) && (
            <RouteButtonLink
              startIcon={<Icon icon={mdiPlus} />}
              to="/delivery_date_change_requests/new"
            >
              {t("deliveryDateChangeRequests.buttons.newRequest")}
            </RouteButtonLink>
          )}
        </Box>

        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleTabChange} value={tab}>
              <RouteTabLink
                from="/delivery_date_change_requests/"
                label={t("deliveryDateChangeRequests.tabs.pendingApproval")}
                search={(prev) => ({ ...prev, tab: "pending_approval" })}
                to="/delivery_date_change_requests"
                value="pending_approval"
              />
              <RouteTabLink
                from="/delivery_date_change_requests/"
                label={t("deliveryDateChangeRequests.tabs.rejected")}
                search={(prev) => ({ ...prev, tab: "rejected" })}
                to="/delivery_date_change_requests"
                value="rejected"
              />
              <RouteTabLink
                from="/delivery_date_change_requests/"
                label={t("deliveryDateChangeRequests.tabs.processing")}
                search={(prev) => ({ ...prev, tab: "processing" })}
                to="/delivery_date_change_requests"
                value="processing"
              />
              <RouteTabLink
                from="/delivery_date_change_requests/"
                label={t("deliveryDateChangeRequests.tabs.completed")}
                search={(prev) => ({ ...prev, tab: "completed" })}
                to="/delivery_date_change_requests"
                value="completed"
              />
              <RouteTabLink
                from="/delivery_date_change_requests/"
                label={t("deliveryDateChangeRequests.tabs.failed")}
                search={(prev) => ({ ...prev, tab: "failed" })}
                to="/delivery_date_change_requests"
                value="failed"
              />
            </Tabs>
          </Box>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} sx={{ margin: 2 }} />
              </Box>
            }
          >
            <DeliveryDateChangeRequestTable />
          </Suspense>
        </TabContext>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/delivery_date_change_requests/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: DeliveryDateChangeRequest,
  loaderDeps: ({ search }) => ({
    filters: {
      brazilian_vendor: search.brazilianVendor,
      created_by_id: search.createdById,
      plant_id: search.plantId,
      procurement_responsible_id: search.procurementResponsibleId,
      purchase_group_id: search.purchaseGroupId,
      purchase_order_item_number: search.purchaseOrderItemNumber,
      purchase_order_number: search.purchaseOrderNumber,
      status: getStatusFilter(search.tab),
      vendor_id: search.vendorId,
    },
    items_per_page: search.rowsPerPage,
    page: search.page,
  }),
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes(
        "Delivery Date Change Request - List",
      )
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    context.queryClient.prefetchQuery(
      deliveryDateChangeRequestsListQueryOptions(deps),
    );

    return null;
  },
});
